import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  ArrowUp,
  RotateCcw,
  ChevronDown,
  X,
  Pen,
  Copy,
  Check,
  ArrowUpRight,
} from 'lucide-react';
import { Square, ArrowsClockwise } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';

import useLimits from '../plan/hooks/limits';
import { askMultiFileQuestionStream } from '../../services/multiFileAskAIService';
import {
  getBubbleBaseStyles,
  getBubbleRoleStyles,
} from '../selectedVideo/chat/history/bubbleStyles';
import { ReferenceButton } from './ReferenceButton';
import { useUserTags } from '../../hooks/useTags';
import { getFile } from '../../services/fileSystemService';
import { formatTimestamp } from '../../utils/timeFormat';
import { FileType } from '../../types';

interface EntityAskAIProps {
  entityName: string;
  entityType: 'person' | 'company';
  fileIds: string[];
  teamId: string;
  getSuggestions: (entityName: string) => string[];
}

interface SavedConversation {
  id: string;
  name: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  fileIds: string[];
  timestamp: number;
  entityKey?: string; // Added to track which entity this conversation belongs to
  entityName?: string;
  entityType?: string;
}

interface ConversationHistoryData {
  [entityKey: string]: SavedConversation[];
}

export const EntityAskAI: React.FC<EntityAskAIProps> = ({
  entityName,
  entityType,
  fileIds,
  teamId,
  getSuggestions,
}) => {
  const { t } = useTranslation();
  const guardLimits = useLimits();
  const { getTag, setTag } = useUserTags();

  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [filesCache, setFilesCache] = useState<Record<string, FileType>>({});
  const [transcriptView, setTranscriptView] = useState<{
    messageIndex: number;
    fileId: string;
    timestamp: string;
  } | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string>(
    () => `conv-${Date.now()}`,
  );
  const [currentConversationEntityKey, setCurrentConversationEntityKey] =
    useState<string>(() => `${entityType}-${entityName}`);
  const [currentConversationFileIds, setCurrentConversationFileIds] =
    useState<string[]>(fileIds);
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const lastScrollTopRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoScrollingRef = useRef(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
    null,
  );
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );
  const [editedContent, setEditedContent] = useState('');
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Detect if user is near bottom of scroll container
  const isNearBottom = useCallback((container: HTMLElement) => {
    const threshold = 150; // pixels from bottom
    const position = container.scrollTop + container.clientHeight;
    const height = container.scrollHeight;
    return height - position < threshold;
  }, []);

  // Track user scrolling (ignore auto-scroll events)
  useEffect(() => {
    const scrollElement = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]',
    ) as HTMLElement;
    if (!scrollElement) return undefined;

    const handleScroll = () => {
      // Ignore scroll events triggered by auto-scroll
      if (isAutoScrollingRef.current) {
        return;
      }

      const currentScrollTop = scrollElement.scrollTop;

      // User scrolled up significantly (more than 5px to avoid jitter)
      if (currentScrollTop < lastScrollTopRef.current - 5) {
        setUserHasScrolled(true);
      }

      // User scrolled near bottom, resume auto-scroll
      if (isNearBottom(scrollElement)) {
        setUserHasScrolled(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [isNearBottom]);

  // Reset scroll flag when conversation history changes (new message)
  useEffect(() => {
    setUserHasScrolled(false);
  }, [conversation.length]);

  // Throttled auto-scroll during streaming
  useEffect(() => {
    if (!isLoading || userHasScrolled) {
      return undefined;
    }

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Throttle scroll updates to every 150ms
    scrollTimeoutRef.current = setTimeout(() => {
      if (messagesEndRef.current && !userHasScrolled) {
        isAutoScrollingRef.current = true;
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });

        // Reset flag after scroll animation completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 400);
      }
    }, 150);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [conversation, isLoading, userHasScrolled]);

  // Scroll to bottom when response generation completes
  useEffect(() => {
    if (!isLoading && conversation.length > 0 && messagesEndRef.current) {
      // Small delay to ensure DOM has updated
      const scrollTimeout = setTimeout(() => {
        if (messagesEndRef.current) {
          isAutoScrollingRef.current = true;
          messagesEndRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });

          // Reset scroll flag to allow auto-scroll for next message
          setUserHasScrolled(false);

          // Reset flag after scroll animation completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 400);
        }
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
    return undefined;
  }, [isLoading, conversation.length]);

  // Helper to get entity key for storage
  const getEntityKey = () => `${entityType}-${entityName}`;

  // Save conversation to history
  const saveCurrentConversation = useCallback(() => {
    if (conversation.length === 0) return;

    const conversationName = conversation[0]?.content || 'Untitled';
    const newSavedConversation: SavedConversation = {
      id: currentConversationId,
      name: conversationName,
      messages: conversation,
      fileIds: currentConversationFileIds,
      timestamp: Date.now(),
    };

    const historyString = getTag('entity-ask-ai-history');
    let allHistory: ConversationHistoryData = {};
    if (historyString) {
      try {
        allHistory = JSON.parse(historyString as string);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
      }
    }

    // Use the tracked entity key instead of the current entity
    const entityKey = currentConversationEntityKey;
    const entityConversations = allHistory[entityKey] || [];

    // Check if conversation already exists (by id) and update it, otherwise add new
    const existingIndex = entityConversations.findIndex(
      (conv) => conv.id === currentConversationId,
    );
    if (existingIndex >= 0) {
      entityConversations[existingIndex] = newSavedConversation;
    } else {
      entityConversations.push(newSavedConversation);
    }

    allHistory[entityKey] = entityConversations;
    setTag({
      key: 'entity-ask-ai-history',
      value: JSON.stringify(allHistory),
    });

    // Update savedConversations with ALL conversations globally
    const allConversations: SavedConversation[] = [];
    Object.entries(allHistory).forEach(([key, conversations]) => {
      const [entType, ...entNameParts] = key.split('-');
      const entName = entNameParts.join('-');

      conversations.forEach((conv) => {
        allConversations.push({
          ...conv,
          entityKey: key,
          entityName: entName,
          entityType: entType,
        });
      });
    });
    setSavedConversations(allConversations);
  }, [
    conversation,
    currentConversationId,
    currentConversationEntityKey,
    currentConversationFileIds,
  ]);

  // Load a conversation from history
  const loadConversation = (conversationId: string) => {
    const conversationToLoad = savedConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (conversationToLoad) {
      setConversation(conversationToLoad.messages);
      setCurrentConversationId(conversationId);
      // Set the entity key to the original conversation's entity
      setCurrentConversationEntityKey(
        conversationToLoad.entityKey || getEntityKey(),
      );
      // Set the fileIds to the original conversation's files
      setCurrentConversationFileIds(conversationToLoad.fileIds || []);
      setQuestion('');
      setTranscriptView(null);
    }
  };

  // Delete a conversation from history
  const deleteConversation = (conversationId: string, e?: React.MouseEvent) => {
    // Prevent the dropdown item click from firing
    e?.stopPropagation();

    const conversationToDelete = savedConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (!conversationToDelete) return;

    const historyString = getTag('entity-ask-ai-history');
    let allHistory: ConversationHistoryData = {};
    if (historyString) {
      try {
        allHistory = JSON.parse(historyString as string);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
        return;
      }
    }

    const entityKey = conversationToDelete.entityKey || getEntityKey();
    const entityConversations = allHistory[entityKey] || [];

    // Remove the conversation
    const updatedEntityConversations = entityConversations.filter(
      (conv) => conv.id !== conversationId,
    );

    if (updatedEntityConversations.length === 0) {
      // If no conversations left for this entity, remove the entity key entirely
      delete allHistory[entityKey];
    } else {
      allHistory[entityKey] = updatedEntityConversations;
    }

    // Save updated history
    setTag({
      key: 'entity-ask-ai-history',
      value: JSON.stringify(allHistory),
    });

    // Update local state with all conversations globally
    const allConversations: SavedConversation[] = [];
    Object.entries(allHistory).forEach(([key, conversations]) => {
      const [entType, ...entNameParts] = key.split('-');
      const entName = entNameParts.join('-');

      conversations.forEach((conv) => {
        allConversations.push({
          ...conv,
          entityKey: key,
          entityName: entName,
          entityType: entType,
        });
      });
    });
    setSavedConversations(allConversations);

    // If we deleted the currently active conversation, start a new one
    if (conversationId === currentConversationId) {
      setConversation([]);
      setQuestion('');
      setCurrentConversationId(`conv-${Date.now()}`);
      setCurrentConversationEntityKey(getEntityKey());
      setCurrentConversationFileIds(fileIds);
      setTranscriptView(null);
    }
  };

  // Load conversation history on mount - GLOBAL across all entities
  useEffect(() => {
    const historyString = getTag('entity-ask-ai-history');
    if (historyString) {
      try {
        const allHistory: ConversationHistoryData = JSON.parse(
          historyString as string,
        );

        // Flatten all conversations from all entities into a single array
        const allConversations: SavedConversation[] = [];
        Object.entries(allHistory).forEach(([entityKey, conversations]) => {
          // Add entity metadata to each conversation
          const [convEntityType, ...entityNameParts] = entityKey.split('-');
          const convEntityName = entityNameParts.join('-');

          conversations.forEach((conv) => {
            allConversations.push({
              ...conv,
              entityKey,
              entityName: convEntityName,
              entityType: convEntityType,
            });
          });
        });

        setSavedConversations(allConversations);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
      }
    }
  }, [getTag]);

  // Auto-save conversation whenever it changes
  useEffect(() => {
    if (conversation.length > 0) {
      saveCurrentConversation();
    }
  }, [
    conversation,
    currentConversationId,
    currentConversationEntityKey,
    currentConversationFileIds,
    saveCurrentConversation,
  ]);

  // When entity changes (navigating to different person/company), start fresh conversation
  useEffect(() => {
    const newEntityKey = getEntityKey();
    if (
      newEntityKey !== currentConversationEntityKey &&
      conversation.length > 0
    ) {
      // Save current conversation before switching
      saveCurrentConversation();
      // Start fresh for new entity
      setConversation([]);
      setQuestion('');
      setCurrentConversationId(`conv-${Date.now()}`);
      setCurrentConversationEntityKey(newEntityKey);
      setCurrentConversationFileIds(fileIds);
      setTranscriptView(null);
    } else if (conversation.length === 0) {
      // If no conversation yet, just update the entity key and fileIds
      setCurrentConversationEntityKey(newEntityKey);
      setCurrentConversationFileIds(fileIds);
    }
  }, [entityName, entityType]);

  // When fileIds prop changes for current entity, update current conversation fileIds
  useEffect(() => {
    // Only update if we're on a new conversation (not loading an old one)
    const currentEntityKey = getEntityKey();
    if (
      currentConversationEntityKey === currentEntityKey &&
      conversation.length === 0
    ) {
      setCurrentConversationFileIds(fileIds);
    }
  }, [fileIds]);

  // Pre-fetch files referenced in conversation messages
  useEffect(() => {
    const fetchFilesFromConversation = async () => {
      // Extract all fileIds from conversation messages
      const referencedFileIds = new Set<string>();
      conversation.forEach((message) => {
        if (message.role === 'assistant') {
          // Match both raw format [UUID]@timestamp and markdown format [→](UUID@timestamp)
          const rawRegex = /\[([a-f0-9-]{36})\]@\d+/g;
          const markdownRegex = /\[.*?\]\(([a-f0-9-]{36})@\d+\)/g;

          // Try raw format first
          let matches = message.content.matchAll(rawRegex);
          // eslint-disable-next-line no-restricted-syntax
          for (const match of matches) {
            referencedFileIds.add(match[1]);
          }

          // Then try markdown format
          matches = message.content.matchAll(markdownRegex);
          // eslint-disable-next-line no-restricted-syntax
          for (const match of matches) {
            referencedFileIds.add(match[1]);
          }
        }
      });

      // Fetch files that aren't in cache yet
      const filesToFetch = Array.from(referencedFileIds).filter(
        (fileId) => !filesCache[fileId],
      );

      if (filesToFetch.length > 0) {
        const filePromises = filesToFetch.map(async (fileId) => {
          try {
            const fileData = await getFile(fileId);
            if (fileData?.file) {
              return { fileId, file: fileData.file };
            }
          } catch (error) {
            console.error(`Failed to fetch file ${fileId}:`, error);
          }
          return null;
        });

        const results = await Promise.all(filePromises);
        const newFiles: Record<string, FileType> = {};
        results.forEach((result) => {
          if (result) {
            newFiles[result.fileId] = result.file;
          }
        });

        if (Object.keys(newFiles).length > 0) {
          setFilesCache((prev) => ({ ...prev, ...newFiles }));
        }
      }
    };

    if (conversation.length > 0) {
      fetchFilesFromConversation();
    }
  }, [conversation]);

  // Stop the currently running generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  // Edit an existing user message and regenerate response
  const handleEditQuery = async (messageIndex: number, newContent: string) => {
    const userMessage = conversation[messageIndex];

    if (userMessage && userMessage.role === 'user') {
      // Truncate conversation at this point (remove this message and all after)
      const truncatedConversation = conversation.slice(0, messageIndex);
      setConversation(truncatedConversation);

      // Add the edited user message
      const editedUserMessage = { role: 'user' as const, content: newContent };
      setConversation((prev) => [...prev, editedUserMessage]);

      // Clear editing state
      setEditingMessageIndex(null);
      setEditedContent('');

      setIsLoading(true);

      try {
        // Create AbortController for cancellation
        abortControllerRef.current = new AbortController();

        // Use streaming endpoint for real-time responses
        await askMultiFileQuestionStream(
          teamId,
          {
            fileIds: currentConversationFileIds,
            question: newContent,
          },
          (accumulatedText: string) => {
            // Use flushSync to force immediate render for smooth streaming animation
            flushSync(() => {
              setConversation((prev) => {
                const newConv = [...prev];
                if (
                  newConv.length > 0 &&
                  newConv[newConv.length - 1].role === 'assistant'
                ) {
                  // Update existing assistant message
                  newConv[newConv.length - 1] = {
                    role: 'assistant',
                    content: accumulatedText,
                  };
                } else {
                  // Add new assistant message
                  newConv.push({
                    role: 'assistant',
                    content: accumulatedText,
                  });
                }
                return newConv;
              });
            });
          },
          { signal: abortControllerRef.current.signal },
        );

        // Clear the abort controller after successful completion
        abortControllerRef.current = null;
      } catch (error) {
        console.error('Failed to regenerate response:', error);

        // Clear the abort controller
        abortControllerRef.current = null;

        // Check if error was due to abort
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Request was cancelled by user');
        } else {
          // Remove the user message and any partial assistant response on error
          setConversation((prev) => {
            const newConv = [...prev];
            // Remove last message if it's an assistant message (partial response)
            if (
              newConv.length > 0 &&
              newConv[newConv.length - 1].role === 'assistant'
            ) {
              newConv.pop();
            }
            // Remove the user message
            if (
              newConv.length > 0 &&
              newConv[newConv.length - 1].role === 'user'
            ) {
              newConv.pop();
            }
            return newConv;
          });
          toast.error(
            error instanceof Error
              ? error.message
              : t(
                  `${entityType}.askAI.error`,
                  'Failed to regenerate response. Please try again.',
                ),
          );
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Regenerate an assistant response
  const handleRegenerateResponse = async (messageIndex: number) => {
    const assistantMessage = conversation[messageIndex];

    if (assistantMessage && assistantMessage.role === 'assistant') {
      // Find the corresponding user message (should be right before this assistant message)
      const userMessageIndex = messageIndex - 1;
      const userMessage = conversation[userMessageIndex];

      if (userMessage && userMessage.role === 'user') {
        // Truncate conversation at the assistant message (keeping the user message)
        const truncatedConversation = conversation.slice(0, messageIndex);

        // Use flushSync to ensure state updates happen immediately before streaming starts
        flushSync(() => {
          setConversation(truncatedConversation);
          setIsLoading(true);
        });

        try {
          // Create AbortController for cancellation
          abortControllerRef.current = new AbortController();

          // Use streaming endpoint with the original user message
          await askMultiFileQuestionStream(
            teamId,
            {
              fileIds: currentConversationFileIds,
              question: userMessage.content,
            },
            (accumulatedText: string) => {
              // Use flushSync to force immediate render for smooth streaming animation
              flushSync(() => {
                setConversation((prev) => {
                  const newConv = [...prev];
                  if (
                    newConv.length > 0 &&
                    newConv[newConv.length - 1].role === 'assistant'
                  ) {
                    // Update existing assistant message
                    newConv[newConv.length - 1] = {
                      role: 'assistant',
                      content: accumulatedText,
                    };
                  } else {
                    // Add new assistant message
                    newConv.push({
                      role: 'assistant',
                      content: accumulatedText,
                    });
                  }
                  return newConv;
                });
              });
            },
            { signal: abortControllerRef.current.signal },
          );

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;
        } catch (error) {
          console.error('Failed to regenerate response:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was cancelled by user');
          } else {
            toast.error(
              error instanceof Error
                ? error.message
                : t(
                    `${entityType}.askAI.error`,
                    'Failed to regenerate response. Please try again.',
                  ),
            );
          }
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handleAskQuestion = async (questionText?: string) => {
    const finalQuestion = questionText || question;

    if (!finalQuestion.trim()) {
      toast.error(
        t(`${entityType}.askAI.enterQuestion`, 'Please enter a question'),
      );
      return;
    }

    if (currentConversationFileIds.length === 0) {
      toast.error(
        t(
          `${entityType}.askAI.noRecordings`,
          `No recordings available for this ${entityType}`,
        ),
      );
      return;
    }

    try {
      guardLimits('multipleRecordingAskAI', async () => {
        // Add user message to conversation immediately
        const userMessage = { role: 'user' as const, content: finalQuestion };
        setConversation((prev) => [...prev, userMessage]);
        setQuestion(''); // Clear input immediately

        // Reset textarea height to single line
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }

        setIsLoading(true);

        try {
          // Create AbortController for cancellation
          abortControllerRef.current = new AbortController();

          // Use streaming endpoint for real-time responses
          await askMultiFileQuestionStream(
            teamId,
            {
              fileIds: currentConversationFileIds,
              question: finalQuestion,
            },
            (accumulatedText: string) => {
              // Use flushSync to force immediate render for smooth streaming animation
              flushSync(() => {
                // Update the last message in conversation with streaming text
                setConversation((prev) => {
                  const newConv = [...prev];
                  // Check if last message is assistant message
                  if (
                    newConv.length > 0 &&
                    newConv[newConv.length - 1].role === 'assistant'
                  ) {
                    // Update existing assistant message
                    newConv[newConv.length - 1] = {
                      role: 'assistant',
                      content: accumulatedText,
                    };
                  } else {
                    // Add new assistant message
                    newConv.push({
                      role: 'assistant',
                      content: accumulatedText,
                    });
                  }
                  return newConv;
                });
              });
            },
            { signal: abortControllerRef.current.signal },
          );

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;
        } catch (error) {
          console.error('Failed to analyze recordings:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            // User cancelled - don't show error toast
            console.log('Request was cancelled by user');
          } else {
            // Remove the user message and any partial assistant response on error
            setConversation((prev) => {
              const newConv = [...prev];
              // Remove last message if it's an assistant message (partial response)
              if (
                newConv.length > 0 &&
                newConv[newConv.length - 1].role === 'assistant'
              ) {
                newConv.pop();
              }
              // Remove the user message
              if (
                newConv.length > 0 &&
                newConv[newConv.length - 1].role === 'user'
              ) {
                newConv.pop();
              }
              return newConv;
            });
            setQuestion(finalQuestion); // Restore question on error
            toast.error(
              error instanceof Error
                ? error.message
                : t(
                    `${entityType}.askAI.error`,
                    'Failed to analyze recordings. Please try again.',
                  ),
            );
          }
        } finally {
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleAskQuestion();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (isLoading) return;
    setQuestion(suggestion);
    handleAskQuestion(suggestion);
  };

  const handleClearChat = () => {
    // Save current conversation before clearing
    if (conversation.length > 0) {
      saveCurrentConversation();
    }
    // Start a new conversation for the current entity
    setConversation([]);
    setQuestion('');
    setCurrentConversationId(`conv-${Date.now()}`);
    setCurrentConversationEntityKey(getEntityKey());
    setCurrentConversationFileIds(fileIds);
    setTranscriptView(null);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, 150);
    e.target.style.height = `${newHeight}px`;
  };

  const getPlaceholder = () => {
    if (currentConversationFileIds.length === 0) {
      return t(
        `${entityType}.askAI.placeholder.noFiles`,
        'No recordings available',
      );
    }
    const recordingText =
      currentConversationFileIds.length === 1 ? 'recording' : 'recordings';
    return t(
      `${entityType}.askAI.placeholder.withFiles`,
      `Ask anything (${currentConversationFileIds.length} ${recordingText})...`,
    );
  };

  // Convert plain text references [UUID]@timestamp to markdown links
  const convertReferencesToMarkdown = (text: string): string => {
    // Match pattern: [UUID]@timestamp where UUID is a standard UUID format
    const referenceRegex = /\[([a-f0-9-]{36})\]@(\d+)/g;
    return text.replace(referenceRegex, '[→]($1@$2)');
  };

  // Helper to categorize conversations by date
  const categorizeConversations = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const categorized = {
      today: [] as SavedConversation[],
      yesterday: [] as SavedConversation[],
      older: [] as SavedConversation[],
    };

    savedConversations
      .sort((a, b) => b.timestamp - a.timestamp)
      .forEach((conv) => {
        const convDate = new Date(conv.timestamp);
        const convDay = new Date(
          convDate.getFullYear(),
          convDate.getMonth(),
          convDate.getDate(),
        );

        if (convDay.getTime() === today.getTime()) {
          categorized.today.push(conv);
        } else if (convDay.getTime() === yesterday.getTime()) {
          categorized.yesterday.push(conv);
        } else {
          categorized.older.push(conv);
        }
      });

    return categorized;
  };

  // Custom markdown components to handle references
  const markdownComponents = () => ({
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // Handle UUID@timestamp references
      if (href?.includes('@')) {
        const [fileId, timestamp] = href.split('@');
        const file = filesCache[fileId];

        return (
          <button
            type="button"
            onClick={() => {
              const baseUrl = window.location.pathname.includes('/app')
                ? '/app/#'
                : '/#';
              window.open(
                `${baseUrl}/library/${file?.teamId || 'unknown'}/recents/${fileId}?t=${timestamp}`,
                '_blank',
              );
            }}
            className={cn(
              'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
            )}
            title={
              file
                ? `Go to: ${file.name} at ${formatTimestamp(timestamp)}`
                : 'Go to source...'
            }
          >
            <ArrowUpRight
              size={18}
              className={cn(
                'relative -top-0.5 inline-block px-0.5 align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
              )}
              strokeWidth={1.5}
            />
          </button>
        );
      }

      // Regular external links
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {children}
        </a>
      );
    },
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-2 last:mb-0">{children}</p>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="mb-2 last:mb-0 ml-4">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="mb-2 last:mb-0 ml-4">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="mb-1">{children}</li>
    ),
  });

  const categorized = categorizeConversations();

  return (
    <div className="flex flex-col h-full border-l border-gray-200 dark:border-gray-700">
      {/* Conversation Display - bottom-aligned */}
      <ScrollArea ref={scrollAreaRef} className="flex-1">
        <div className="min-h-full flex flex-col justify-end px-4 py-3">
          {/* Conversation History Dropdown - Scrollable with content */}
          <div className="pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-between text-sm px-2 h-8"
                >
                  <span className="truncate">
                    {conversation.length > 0 && conversation[0]?.role === 'user'
                      ? conversation[0].content.slice(0, 30) +
                        (conversation[0].content.length > 30 ? '...' : '')
                      : 'New chat'}
                  </span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[300px]">
                {savedConversations.length > 0 ? (
                  <>
                    {categorized.today.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Today
                        </DropdownMenuLabel>
                        {categorized.today.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {categorized.yesterday.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Yesterday
                        </DropdownMenuLabel>
                        {categorized.yesterday.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {categorized.older.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Older
                        </DropdownMenuLabel>
                        {categorized.older.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    No saved conversations
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-col gap-2">
            {conversation.map((message, index) => {
              // Check if this message is showing transcript view
              const isShowingTranscript =
                transcriptView?.messageIndex === index;

              if (isShowingTranscript && transcriptView) {
                return (
                  <ReferenceButton
                    key={`message-${index}`}
                    fileId={transcriptView.fileId}
                    timestamp={transcriptView.timestamp}
                    isActive={false}
                    onShowTranscript={() => {}}
                    inlineMode
                    onBack={() => setTranscriptView(null)}
                  />
                );
              }

              const isEditing = editingMessageIndex === index;

              return (
                <div
                  key={`message-${index}`}
                  className={cn(
                    'group flex items-start gap-1',
                    'animate-fade-in-up',
                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                  )}
                >
                  <div
                    className={cn(
                      'flex flex-col',
                      message.role === 'user' ? 'items-end' : 'items-start',
                      message.role === 'user'
                        ? 'w-full max-w-[95%]'
                        : 'w-full max-w-[85%]',
                    )}
                  >
                    <div
                      className={cn(
                        getBubbleBaseStyles(),
                        getBubbleRoleStyles(message.role),
                        message.role === 'user' &&
                          isEditing &&
                          'min-w-[300px] pb-10',
                      )}
                    >
                      {(() => {
                        if (message.role === 'assistant') {
                          return (
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents()}
                              >
                                {convertReferencesToMarkdown(message.content)}
                              </ReactMarkdown>
                            </div>
                          );
                        }
                        if (isEditing) {
                          return (
                            <Textarea
                              ref={editTextareaRef}
                              value={editedContent}
                              onChange={(e) => {
                                setEditedContent(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  if (editedContent.trim()) {
                                    handleEditQuery(index, editedContent);
                                  }
                                }
                                if (e.key === 'Escape') {
                                  setEditingMessageIndex(null);
                                  setEditedContent('');
                                }
                              }}
                              className="w-full bg-transparent text-primary-foreground border-0 resize-none p-0 m-0 min-h-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 outline-none shadow-none overflow-hidden"
                              style={{
                                lineHeight: '1.5',
                                fontSize: '0.875rem',
                              }}
                              autoFocus
                            />
                          );
                        }
                        return (
                          <div className="whitespace-pre-wrap">
                            {message.content}
                          </div>
                        );
                      })()}

                      {/* Cancel and Send buttons when editing */}
                      {message.role === 'user' && isEditing && (
                        <div className="absolute bottom-2 right-2 flex items-center justify-end gap-1.5 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessageIndex(null);
                              setEditedContent('');
                            }}
                            className="px-2.5 py-1 rounded-full transition-all bg-primary-foreground/60 text-primary hover:bg-primary-foreground/80 text-xs font-medium"
                            title="Cancel editing"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (editedContent.trim()) {
                                handleEditQuery(index, editedContent);
                              }
                            }}
                            disabled={!editedContent.trim()}
                            className={cn(
                              'px-2.5 py-1 rounded-full transition-all text-xs font-medium',
                              editedContent.trim()
                                ? 'bg-primary-foreground text-primary hover:bg-primary-foreground/90'
                                : 'bg-primary-foreground/30 text-primary/50 cursor-not-allowed',
                            )}
                            title="Send edited message"
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Edit button for user messages */}
                    {message.role === 'user' && !isEditing && (
                      <div className="flex items-center gap-1 mr-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMessageIndex(index);
                            setEditedContent(message.content);
                            setTimeout(() => {
                              if (editTextareaRef.current) {
                                editTextareaRef.current.style.height = 'auto';
                                editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
                              }
                            }, 0);
                          }}
                          className="p-1 rounded hover:bg-accent transition-colors"
                          title="Edit message"
                        >
                          <Pen className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}

                    {/* Copy and Regenerate buttons for assistant messages */}
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-1 ml-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(message.content);
                            setCopiedMessageIndex(index);
                            setTimeout(() => setCopiedMessageIndex(null), 2000);
                          }}
                          className="p-1 rounded hover:bg-muted transition-colors"
                          title="Copy message"
                        >
                          {copiedMessageIndex === index ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleRegenerateResponse(index);
                          }}
                          className="p-1 rounded hover:bg-muted transition-colors"
                          title="Regenerate response"
                        >
                          <ArrowsClockwise className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Loading indicator - only show before first chunk arrives */}
            {isLoading &&
              conversation.length > 0 &&
              conversation[conversation.length - 1].role === 'user' && (
                <div
                  className={cn(
                    getBubbleBaseStyles(),
                    getBubbleRoleStyles('assistant'),
                    'max-w-[85%]',
                  )}
                >
                  <div className="flex items-center gap-1 py-2">
                    {[0, 1, 2].map((index) => (
                      <span
                        key={index}
                        className="w-2 h-2 rounded-full bg-muted-foreground opacity-70 animate-pulse"
                        style={{
                          animationDelay: `${index * 0.2}s`,
                          animationDuration: '1.5s',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

            {/* Invisible element at the end for auto-scrolling */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </ScrollArea>

      {/* Suggestions - horizontally scrollable above grey section */}
      {conversation.length === 0 && currentConversationFileIds.length > 0 && (
        <div className="pb-2 flex items-end">
          <div
            className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent pb-2 px-4"
            style={{ scrollbarWidth: 'thin' }}
          >
            {getSuggestions(entityName).map((suggestion, index) => (
              <Button
                key={`suggestion-${index}`}
                variant="outline"
                size="sm"
                className={cn(
                  'flex-shrink-0 h-auto py-1 px-2 text-left whitespace-normal',
                  'border border-border/50 bg-background/50 hover:bg-accent/50',
                  'transition-all duration-200 ease-out',
                  'max-w-[200px] min-w-fit',
                )}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isLoading}
              >
                <span className="text-sm font-medium line-clamp-1 truncate">
                  {suggestion}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area - styled like chat Ask.tsx */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-background">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAskQuestion();
          }}
        >
          <div
            className={cn(
              'bg-white dark:bg-background border shadow-sm',
              'transition-all duration-300 ease-in-out',
              'rounded-[20px]',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
              'px-1.5 py-1 flex items-center gap-1',
            )}
          >
            {/* Textarea */}
            <Textarea
              ref={textareaRef}
              className={cn(
                'flex-1 min-h-[24px] max-h-[150px] resize-none border-0 pl-1 pr-2 py-1',
                'text-sm leading-6 placeholder:text-muted-foreground',
                'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                'overflow-y-auto scrollbar-thin',
              )}
              placeholder={getPlaceholder()}
              value={question}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyPress}
              onFocus={(e) => {
                setIsFocused(true);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
              }}
              onBlur={() => setIsFocused(false)}
              disabled={isLoading || currentConversationFileIds.length === 0}
              rows={1}
            />

            {/* Send/Reset/Stop Button */}
            <div className="flex-shrink-0">
              {(() => {
                const hasText = question.trim();
                const showReset =
                  !hasText && conversation.length > 0 && !isLoading;
                const isDisabled =
                  (!hasText && !showReset) ||
                  currentConversationFileIds.length === 0;

                if (isLoading) {
                  // Show stop button when loading
                  return (
                    <Button
                      type="button"
                      size="icon"
                      onClick={stopGeneration}
                      variant="default"
                      className="h-7 w-7 rounded-full bg-primary hover:bg-primary/90 transition-all duration-200"
                    >
                      <Square
                        className="w-3.5 h-3.5 text-white"
                        weight="fill"
                      />
                    </Button>
                  );
                }

                if (showReset) {
                  // Show reset button when no text and has conversation
                  return (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClearChat}
                      className="h-7 w-7 rounded-full hover:bg-accent transition-all duration-200"
                    >
                      <RotateCcw className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  );
                }

                // Show send button
                const isInactive = !hasText;

                return (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={isDisabled}
                    variant={isInactive ? 'ghost' : 'default'}
                    className={cn(
                      'h-7 w-7 rounded-full transition-all duration-300',
                      isInactive && 'bg-muted hover:bg-muted/80',
                      !isInactive &&
                        'bg-primary hover:bg-primary/90 hover:scale-105',
                    )}
                  >
                    <ArrowUp
                      className={cn(
                        'h-4 w-4 transition-all duration-300',
                        isInactive
                          ? 'text-muted-foreground'
                          : 'text-primary-foreground',
                        !isInactive && 'hover:-translate-y-0.5',
                      )}
                    />
                  </Button>
                );
              })()}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntityAskAI;
